/* Progressive enhancement: content, product links, FAQs and full video work without JS. */
(() => {
  if (!customElements.get('re-motion-player')) {
    customElements.define('re-motion-player', class extends HTMLElement {
      connectedCallback() {
        if (this.abort) return;
        this.abort = new AbortController();
        this.video = this.querySelector('video');
        this.toggle = this.querySelector('[data-video-toggle]');
        if (!this.video || !this.toggle) return;
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.manualPause = false;
        this.toggle.hidden = false;
        const signal = this.abort.signal;
        const update = () => {
          const paused = this.video.paused;
          this.toggle.setAttribute('aria-label', paused ? 'Play motion preview' : 'Pause motion preview');
          this.querySelector('[data-video-label]').textContent = paused ? 'Play preview' : 'Pause preview';
          this.querySelector('[data-video-icon]').textContent = paused ? '▶' : 'Ⅱ';
        };
        this.video.addEventListener('play', update, { signal });
        this.video.addEventListener('pause', update, { signal });
        this.video.addEventListener('error', () => {
          this.toggle.hidden = true;
          this.video.removeAttribute('src');
        }, { signal });
        this.toggle.addEventListener('click', () => {
          if (this.video.paused) { this.manualPause = false; this.play(); }
          else { this.manualPause = true; this.video.pause(); }
        }, { signal });
        this.motion.addEventListener('change', () => { if (this.motion.matches) this.video.pause(); }, { signal });
        this.observer = new IntersectionObserver(entries => {
          for (const entry of entries) {
            if (!entry.isIntersecting) this.video.pause();
            else if (this.dataset.autoplay === 'true' && !this.motion.matches && !navigator.connection?.saveData && !this.manualPause) this.play();
          }
        }, { threshold: 0.2 });
        this.observer.observe(this);
        document.addEventListener('visibilitychange', () => { if (document.hidden) this.video.pause(); }, { signal });
      }
      play() {
        if (!this.video.getAttribute('src')) this.video.src = this.video.dataset.src;
        this.video.muted = true;
        this.video.play().catch(() => { /* Keep the poster and explicit play control. */ });
      }
      disconnectedCallback() { this.observer?.disconnect(); this.abort?.abort(); this.abort = null; this.video?.pause(); }
    });
  }
  if (!customElements.get('re-motion-tabs')) {
    customElements.define('re-motion-tabs', class extends HTMLElement {
      connectedCallback() {
        if (this.abort) return;
        this.abort = new AbortController();
        const signal = this.abort.signal;
        this.tabs = [...this.querySelectorAll('[data-tab]')];
        this.panels = [...this.querySelectorAll('[data-panel]')];
        if (!this.tabs.length) return;
        this.dataset.enhanced = 'true';
        this.querySelector('[role=tablist]').hidden = false;
        this.panels.forEach((panel, i) => {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', this.tabs[i].id);
          panel.setAttribute('tabindex', '0');
        });
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i), { signal });
          tab.addEventListener('keydown', event => {
            let next = i;
            if (event.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
            else if (event.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = this.tabs.length - 1;
            else return;
            event.preventDefault(); this.select(next); this.tabs[next].focus();
          }, { signal });
        });
        this.addEventListener('shopify:block:select', event => {
          const index = this.panels.findIndex(p => p === event.target || p.contains(event.target));
          if (index >= 0) this.select(index);
        }, { signal });
        this.select(0);
      }
      select(index) {
        this.tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
        this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
      }
      disconnectedCallback() { this.abort?.abort(); this.abort = null; }
    });
  }
})();
